import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { BoundGrabHandle, BoundLabel } from '@examdojo/core/widget';
import type { PortMap, PointPort, EdgePort } from '../../../port';
import type { PlacedShape } from '../../../placement';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import { resolveGeometryStyle } from '../../coloring';
import type { GeometryStyleContext } from '../../coloring';
import type { PointElement, LineElement, PolygonElement } from './primitives.model';

// =============================================================================
// POINT CREATOR
// =============================================================================

/**
 * Create a point element on the board.
 *
 * Fixed/free is controlled via display config (PointStyle.fixed, default: true).
 * Glide behavior is controlled via Point.glideOn param (passed as glideTarget).
 */
export function createPointElement(
  element: PointElement & { id: string },
  placed: PlacedShape,
  context: GeometryPadElementContext,
  glideTarget?: string,
): CreatedGeometryElement {
  const { elementFactory, portRegistry, displayConfig, geometryConfig } = context;
  const jsxObjects: JXGElement[] = [];
  const labels: BoundLabel[] = [];
  const grabHandles: BoundGrabHandle[] = [];

  const coords = placed.vertices['point'] as [number, number];
  const elementDisplay = displayConfig.elements?.[element.id];
  const styleCtx: GeometryStyleContext = {
    elementFactory,
    shapeTheme: context.shapeTheme,
    geometryConfig,
  };
  const pointStyle = resolveGeometryStyle('point', 'Point', styleCtx, elementDisplay);

  let point: JXGElement;

  if (glideTarget) {
    // Glider mode: constrained to slide on another element
    const slideObject = portRegistry.resolveElement(glideTarget) as JXGElement;
    const showGrabHandle = elementDisplay?.point?.showGrabHandle ?? true;
    const gliderResult = elementFactory.glider({
      coords,
      slideObject,
      name: element.id,
      style: {
        visible: elementDisplay?.point?.visible ?? elementDisplay?.visible ?? true,
        fixed: false,
        color: pointStyle.color,
        size: pointStyle.size,
        label: elementDisplay?.point?.label ?? { visible: true, contentVariant: 'name', variant: 'primary' },
        showGrabHandle,
      },
    });
    point = gliderResult.glider;
    labels.push(gliderResult.label);
    if (gliderResult.grabHandle) {
      jsxObjects.push(gliderResult.grabHandle.element);
      grabHandles.push(gliderResult.grabHandle);
    }
  } else {
    const isFixed = elementDisplay?.point?.fixed ?? true;
    const showGrabHandle = elementDisplay?.point?.showGrabHandle ?? !isFixed;
    const pointResult = elementFactory.point({
      coords,
      name: element.id,
      style: {
        visible: elementDisplay?.point?.visible ?? elementDisplay?.visible ?? true,
        fixed: isFixed,
        color: pointStyle.color,
        size: pointStyle.size,
        label: elementDisplay?.point?.label ?? { visible: true, contentVariant: 'name', variant: 'primary' },
        showGrabHandle,
      },
    });
    point = pointResult.point;
    labels.push(pointResult.label);
    if (pointResult.grabHandle) {
      jsxObjects.push(pointResult.grabHandle.element);
      grabHandles.push(pointResult.grabHandle);
    }
  }

  jsxObjects.push(point);

  const typedPoint = point as unknown as { X: () => number; Y: () => number };

  const ports: PortMap = {
    point: () => [typedPoint.X(), typedPoint.Y()] as PointPort,
    x: () => typedPoint.X(),
    y: () => typedPoint.Y(),
  };

  return {
    jsxObjects,
    labels,
    ports,
    grabHandles: grabHandles.length > 0 ? grabHandles : undefined,
    destroy: () => {
      for (const gh of grabHandles) {
        gh.destroy();
      }
      const board = elementFactory.getContext().board;
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}

// =============================================================================
// LINE CREATOR
// =============================================================================

/**
 * Create a line element on the board.
 *
 * Line mode (segment/ray/infinite) is controlled via display config:
 * - straightFirst/straightLast in LineStyle (default: both false = segment)
 */
export function createLineElement(
  element: LineElement & { id: string },
  placed: PlacedShape,
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const { elementFactory, displayConfig, geometryConfig } = context;
  const jsxObjects: JXGElement[] = [];
  const labels: BoundLabel[] = [];

  const from = placed.vertices['from'] as [number, number];
  const to = placed.vertices['to'] as [number, number];
  const elementDisplay = displayConfig.elements?.[element.id];
  const styleCtx: GeometryStyleContext = {
    elementFactory,
    shapeTheme: context.shapeTheme,
    geometryConfig,
  };
  const lineStyle = resolveGeometryStyle('line', 'Line', styleCtx, elementDisplay);

  const lineResult = elementFactory.line({
    a: from,
    b: to,
    name: element.id,
    style: {
      color: lineStyle.color,
      strokeWidth: lineStyle.strokeWidth,
      straightFirst: elementDisplay?.line?.straightFirst ?? false,
      straightLast: elementDisplay?.line?.straightLast ?? false,
      visible: elementDisplay?.visible ?? true,
      label: elementDisplay?.line?.label ?? false,
      ...(elementDisplay?.line ?? {}),
    },
    vertexNames: [`${element.id}_from`, `${element.id}_to`],
  });

  jsxObjects.push(lineResult.line, lineResult.a, lineResult.b);
  jsxObjects.push(...lineResult.helpers);
  labels.push(lineResult.lineLabel);

  const pFrom = lineResult.a as unknown as { X: () => number; Y: () => number };
  const pTo = lineResult.b as unknown as { X: () => number; Y: () => number };

  const ports: PortMap = {
    from: () => [pFrom.X(), pFrom.Y()] as PointPort,
    to: () => [pTo.X(), pTo.Y()] as PointPort,
    length: () => {
      const dx = pTo.X() - pFrom.X();
      const dy = pTo.Y() - pFrom.Y();
      return Math.sqrt(dx * dx + dy * dy);
    },
    'edge.line': () => ({ start: [pFrom.X(), pFrom.Y()], end: [pTo.X(), pTo.Y()] }) as EdgePort,
  };

  return {
    jsxObjects,
    labels,
    ports,
    destroy: () => {
      const board = elementFactory.getContext().board;
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}

// =============================================================================
// POLYGON CREATOR
// =============================================================================

/**
 * Create a generic polygon element on the board.
 * Vertices can be static coordinates or references to other element points.
 */
export function createPolygonElement(
  element: PolygonElement & { id: string },
  placed: PlacedShape,
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const { elementFactory, displayConfig, geometryConfig } = context;
  const jsxObjects: JXGElement[] = [];
  const labels: BoundLabel[] = [];

  const elementDisplay = displayConfig.elements?.[element.id];
  const styleCtx: GeometryStyleContext = {
    elementFactory,
    shapeTheme: context.shapeTheme,
    geometryConfig,
  };

  // Collect vertex inputs - either coordinates or existing JSXGraph point elements
  const vertexInputs: Array<[number, number] | JXGElement> = [];

  for (let i = 0; i < element.params.vertices.length; i++) {
    const vertexKey = `point[${i}]`;
    const placedVertex = placed.vertices[vertexKey];

    if (placedVertex) {
      // Use placed coordinates (resolved during canonical placement)
      vertexInputs.push(placedVertex as [number, number]);
    }
  }

  const polyStyle = resolveGeometryStyle('polygon', 'Polygon', styleCtx, elementDisplay);
  const borderStyle = resolveGeometryStyle('line', 'Polygon', styleCtx, elementDisplay);
  const polygon = elementFactory.polygon({
    vertices: vertexInputs,
    name: element.id,
    style: {
      fillColor: polyStyle.fillColor,
      fillOpacity: polyStyle.fillOpacity,
      strokeWidth: 0,
      vertex: { visible: false, fixed: true, label: false },
      border: {
        strokeWidth: borderStyle.strokeWidth ?? 2,
        color: borderStyle.color,
      },
    },
  });

  jsxObjects.push(polygon.polygon, ...polygon.vertices, ...polygon.borders);

  // Build ports
  const ports: PortMap = {};

  for (let i = 0; i < polygon.vertices.length; i++) {
    const p = polygon.vertices[i] as unknown as { X: () => number; Y: () => number };
    ports[`point[${i}]`] = () => [p.X(), p.Y()] as PointPort;
  }

  for (let i = 0; i < polygon.borders.length; i++) {
    const j = (i + 1) % polygon.vertices.length;
    const pi = polygon.vertices[i] as unknown as { X: () => number; Y: () => number };
    const pj = polygon.vertices[j] as unknown as { X: () => number; Y: () => number };

    ports[`edge[${i}]`] = () => ({ start: [pi.X(), pi.Y()], end: [pj.X(), pj.Y()] }) as EdgePort;

    // Also provide edge length
    ports[`edge[${i}].length`] = () => {
      const dx = pj.X() - pi.X();
      const dy = pj.Y() - pi.Y();
      return Math.sqrt(dx * dx + dy * dy);
    };
  }

  // Computed ports
  const typedPolygon = polygon.polygon as unknown as { Area: () => number };
  ports['area'] = () => Math.abs(typedPolygon.Area());
  ports['perimeter'] = () => {
    let perim = 0;
    for (let i = 0; i < polygon.vertices.length; i++) {
      const j = (i + 1) % polygon.vertices.length;
      const pi = polygon.vertices[i] as unknown as { X: () => number; Y: () => number };
      const pj = polygon.vertices[j] as unknown as { X: () => number; Y: () => number };
      const dx = pj.X() - pi.X();
      const dy = pj.Y() - pi.Y();
      perim += Math.sqrt(dx * dx + dy * dy);
    }
    return perim;
  };

  return {
    jsxObjects,
    labels,
    ports,
    destroy: () => {
      const board = elementFactory.getContext().board;
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}
